import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { getVariants } from '../../app/actions/menu';
import { MenusAPIResponse, StoreMenuResult, StoreError, ResponseMetadata } from '../../types/menuApi';

interface MenuState {
  products: any[];
  bundles: any[];
  modifiers: any[];
  optionTypes: any[];
  slots: any[];
  variants: any[];
  activeMenus: {
    success: StoreMenuResult[];
    failed: StoreError[];
    metadata: ResponseMetadata | null;
    count: number;
    page: number;
    pages: number;
  };
  inactiveMenus: {
    success: StoreMenuResult[];
    failed: StoreError[];
    metadata: ResponseMetadata | null;
    count: number;
    page: number;
    pages: number;
  };
  loading: {
    products: boolean;
    bundles: boolean;
    modifiers: boolean;
    optionTypes: boolean;
    slots: boolean;
    variants: boolean;
    activeMenus: boolean;
    inactiveMenus: boolean;
  };
  error: {
    products: string | null;
    bundles: string | null;
    modifiers: string | null;
    optionTypes: string | null;
    slots: string | null;
    variants: string | null;
    activeMenus: string | null;
    inactiveMenus: string | null;
  };
}

const initialState: MenuState = {
  products: [],
  bundles: [],
  modifiers: [],
  optionTypes: [],
  slots: [],
  variants: [],
  activeMenus: {
    success: [],
    failed: [],
    metadata: null,
    count: 0,
    page: 0,
    pages: 0,
  },
  inactiveMenus: {
    success: [],
    failed: [],
    metadata: null,
    count: 0,
    page: 0,
    pages: 0,
  },
  loading: {
    products: false,
    bundles: false,
    modifiers: false,
    optionTypes: false,
    slots: false,
    variants: false,
    activeMenus: false,
    inactiveMenus: false,
  },
  error: {
    products: null,
    bundles: null,
    modifiers: null,
    optionTypes: null,
    slots: null,
    variants: null,
    activeMenus: null,
    inactiveMenus: null,
  },
};

export const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setProducts: (state, action: PayloadAction<any[]>) => {
      state.products = action.payload;
    },
    setBundles: (state, action: PayloadAction<any[]>) => {
      state.bundles = action.payload;
    },
    setModifiers: (state, action: PayloadAction<any[]>) => {
      state.modifiers = action.payload;
    },
    setOptionTypes: (state, action: PayloadAction<any[]>) => {
      state.optionTypes = action.payload;
    },
    setSlots: (state, action: PayloadAction<any[]>) => {
      state.slots = action.payload;
    },
    setVariants: (state, action: PayloadAction<any[]>) => {
      state.variants = action.payload;
    },
    setActiveMenus: (state, action: PayloadAction<MenusAPIResponse>) => {
      state.activeMenus = {
        success: action.payload.success,
        failed: action.payload.failed,
        metadata: action.payload.metadata,
        count: action.payload.count,
        page: action.payload.page,
        pages: action.payload.pages,
      };
    },
    setInactiveMenus: (state, action: PayloadAction<MenusAPIResponse>) => {
      state.inactiveMenus = {
        success: action.payload.success,
        failed: action.payload.failed,
        metadata: action.payload.metadata,
        count: action.payload.count,
        page: action.payload.page,
        pages: action.payload.pages,
      };
    },
    // Legacy action for backward compatibility during migration
    setActiveMenusLegacy: (state, action: PayloadAction<{ menus: any[]; count: number; page: number; pages: number }>) => {
      state.activeMenus = {
        success: action.payload.menus.map((menu, index) => ({
          storeNumber: menu.storeNumber || `store-${index}`,
          menuData: menu,
          menuType: 'active' as const,
          retrievedAt: new Date().toISOString(),
        })),
        failed: [],
        metadata: {
          totalRequested: action.payload.count,
          successCount: action.payload.menus.length,
          failureCount: 0,
          partialSuccess: false,
          processingTime: '0ms',
          failuresByType: {},
        },
        count: action.payload.count,
        page: action.payload.page,
        pages: action.payload.pages,
      };
    },
    // Legacy action for backward compatibility during migration
    setInactiveMenusLegacy: (state, action: PayloadAction<{ menus: any[]; count: number; page: number; pages: number }>) => {
      state.inactiveMenus = {
        success: action.payload.menus.map((menu, index) => ({
          storeNumber: menu.storeNumber || `store-${index}`,
          menuData: menu,
          menuType: 'inactive' as const,
          retrievedAt: new Date().toISOString(),
        })),
        failed: [],
        metadata: {
          totalRequested: action.payload.count,
          successCount: action.payload.menus.length,
          failureCount: 0,
          partialSuccess: false,
          processingTime: '0ms',
          failuresByType: {},
        },
        count: action.payload.count,
        page: action.payload.page,
        pages: action.payload.pages,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getVariants.pending, (state) => {
      state.loading.variants = true;
      state.error.variants = null;
    });
    builder.addCase(getVariants.fulfilled, (state, action) => {
      state.loading.variants = false;
      state.variants = action.payload.variants || [];
    });
    builder.addCase(getVariants.rejected, (state, action) => {
      state.loading.variants = false;
      state.error.variants = action.error.message || 'Failed to fetch variants';
    });
  },
});

export const { 
  setProducts, 
  setBundles, 
  setModifiers, 
  setOptionTypes, 
  setSlots, 
  setVariants,
  setActiveMenus,
  setInactiveMenus,
  setActiveMenusLegacy,
  setInactiveMenusLegacy
} = menuSlice.actions;

// Selectors
export const selectProducts = (state: RootState) => state.menu.products;
export const selectBundles = (state: RootState) => state.menu.bundles;
export const selectModifiers = (state: RootState) => state.menu.modifiers;
export const selectOptionTypes = (state: RootState) => state.menu.optionTypes;
export const selectSlots = (state: RootState) => state.menu.slots;
export const selectVariants = (state: RootState) => state.menu.variants;
export const selectActiveMenus = (state: RootState) => state.menu.activeMenus;
export const selectInactiveMenus = (state: RootState) => state.menu.inactiveMenus;
export const selectMenuLoading = (state: RootState) => state.menu.loading;
export const selectMenuError = (state: RootState) => state.menu.error;

export default menuSlice.reducer;
